<template>
    <el-drawer :model-value="modelValue" class="dbconsole-blocks-drawer" direction="rtl" size="380px"
        :with-header="false" append-to-body @update:model-value="$emit('update:modelValue', $event)">
        <aside class="dbconsole-right">
            <div class="dbconsole-right__head">
                <span class="dbconsole-title">SQL 块</span>
                <span class="dbconsole-right__count">{{ blockPage.total }}</span>
                <div class="dbconsole-right__switch">
                    <span class="dbconsole-right__switch-label">他人共享</span>
                    <el-switch v-model="showShared" size="small" />
                </div>
                <el-button class="dbconsole-right__close" size="small" text @click="$emit('update:modelValue', false)">
                    <el-icon>
                        <Close />
                    </el-icon>
                </el-button>
            </div>
            <el-input v-model="blockKeyword" class="dbconsole-right__search" size="small" clearable
                placeholder="按名称筛选">
                <template #prefix>
                    <el-icon>
                        <Search />
                    </el-icon>
                </template>
            </el-input>

            <div class="dbconsole-right__list" v-loading="blockLoading">
                <el-empty v-if="rightBlocks.length === 0"
                    :description="blockKeyword ? '没有匹配的 SQL 块' : (showShared ? '暂无他人共享的 SQL 块' : '暂无 SQL 块')"
                    :image-size="60" />
                <div v-for="sb in rightBlocks" :key="sb.id" class="dbconsole-shared">
                    <div class="dbconsole-shared__head">
                        <div class="dbconsole-shared__info">
                            <div class="dbconsole-shared__name">{{ sb.name }}</div>
                            <div v-if="sb.databaseName || sb.isShare || !sb.ownedByMe" class="dbconsole-shared__meta">
                                <el-tag v-if="sb.databaseName" size="small" type="info" effect="plain">
                                    {{ sb.databaseName }}</el-tag>
                                <el-tag v-if="sb.isShare" size="small" type="success" effect="plain">共享</el-tag>
                                <span v-if="!sb.ownedByMe" class="dbconsole-shared__other">{{ sb.ownerName || '他人' }}</span>
                            </div>
                            <div class="dbconsole-shared__desc">{{ sb.description || '—' }}</div>
                        </div>
                        <div class="dbconsole-shared__ops">
                            <el-button class="dbconsole-shared__load" size="small" type="primary" plain
                                @click="$emit('load', sb)">
                                <el-icon class="el-icon--left">
                                    <Download />
                                </el-icon>载入
                            </el-button>
                            <el-dropdown trigger="click" @command="cmd => onCommand(cmd, sb)">
                                <el-button size="small" text @click.stop>
                                    <el-icon>
                                        <MoreFilled />
                                    </el-icon>
                                </el-button>
                                <template #dropdown>
                                    <el-dropdown-menu>
                                        <el-dropdown-item command="favs" class="dbconsole-mi">
                                            <el-icon>
                                                <Collection />
                                            </el-icon>收藏结果（{{ sb.favoriteCount || 0 }}）
                                        </el-dropdown-item>
                                        <el-dropdown-item command="edit" :disabled="!sb.ownedByMe" class="dbconsole-mi">
                                            <el-icon>
                                                <EditPen />
                                            </el-icon>编辑
                                        </el-dropdown-item>
                                        <el-dropdown-item command="share" :disabled="!sb.ownedByMe"
                                            class="dbconsole-mi dbconsole-mi--share">
                                            <el-icon>
                                                <Share />
                                            </el-icon>{{ sb.isShare ? '取消共享' : '共享' }}
                                        </el-dropdown-item>
                                        <el-dropdown-item command="delete" :disabled="!sb.ownedByMe" divided
                                            class="dbconsole-mi dbconsole-mi--delete">
                                            <el-icon>
                                                <Delete />
                                            </el-icon>删除
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </template>
                            </el-dropdown>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="blockPage.total > blockPage.size" class="dbconsole-pager">
                <el-pagination small background layout="prev, pager, next" :total="blockPage.total"
                    :page-size="blockPage.size" :current-page="blockPage.current" :pager-count="5"
                    @current-change="onPageChange" />
            </div>
        </aside>
    </el-drawer>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import debounce from 'lodash/debounce';
import request from '../../../../utils/request.js';

export default {
    name: 'DbSavedBlocksDrawer',
    props: {
        modelValue: { type: Boolean, default: false },
        connId: { type: [String, Number], default: '' }
    },
    emits: ['update:modelValue', 'load', 'edit', 'favs', 'changed', 'total'],
    data() {
        return {
            showShared: false,
            blockKeyword: '',
            rightBlocks: [],
            blockLoading: false,
            blockPage: { current: 1, size: 100, total: 0 },
            initializing: false
        };
    },
    watch: {
        connId: {
            immediate: true,
            handler() {
                this.resetAndLoad();
            }
        },
        showShared() {
            if (this.initializing) return;
            this.reload();
        },
        blockKeyword() {
            if (this.initializing) return;
            this.debouncedReload();
        }
    },
    created() {
        this.debouncedReload = debounce(() => this.reload(), 700);
    },
    methods: {
        resetAndLoad() {
            this.initializing = true;
            this.showShared = false;
            this.blockKeyword = '';
            this.blockPage.current = 1;
            this.rightBlocks = [];
            this.load().finally(() => {
                this.initializing = false;
            });
        },
        load() {
            if (!this.connId) return Promise.resolve();
            this.blockLoading = true;
            return request({
                url: '/dbManSqlBlock/pageList',
                method: 'post',
                data: {
                    connId: this.connId,
                    keyword: this.blockKeyword,
                    includeShared: this.showShared,
                    current: this.blockPage.current,
                    size: this.blockPage.size
                }
            }).then(res => {
                if (res.code == 0) {
                    this.rightBlocks = res.data || [];
                    this.blockPage.total = res.total || 0;
                    this.$emit('total', this.blockPage.total);
                }
            }).finally(() => {
                this.blockLoading = false;
            });
        },
        reload() {
            this.blockPage.current = 1;
            return this.load();
        },
        onPageChange(page) {
            this.blockPage.current = page;
            this.load();
        },
        adjustFavoriteCount(blockId, delta) {
            const sb = this.rightBlocks.find(b => b.id === blockId);
            if (sb) sb.favoriteCount = Math.max(0, (sb.favoriteCount || 0) + delta);
        },
        onCommand(cmd, sb) {
            if (cmd === 'favs') return this.$emit('favs', sb);
            if (cmd === 'edit') return this.openEdit(sb);
            if (cmd === 'share') return this.toggleShare(sb);
            if (cmd === 'delete') return this.confirmDelete(sb);
        },
        openEdit(sb) {
            if (!sb.ownedByMe) {
                ElMessage({ message: '只能编辑自己创建的 SQL 块', type: 'warning', duration: 2000 });
                return;
            }
            this.$emit('edit', sb);
        },
        toggleShare(sb) {
            const next = !sb.isShare;
            request({
                url: '/dbManSqlBlock/share',
                method: 'post',
                data: { id: sb.id, isShare: next }
            }).then(res => {
                if (res.code == 0) {
                    sb.isShare = next;
                    ElMessage({ message: next ? '已共享' : '已取消共享', type: 'success', duration: 1500 });
                    this.$emit('changed');
                } else {
                    ElMessage({ message: res.msg || '操作失败', type: 'error', duration: 3000 });
                }
            });
        },
        confirmDelete(sb) {
            ElMessageBox.confirm(
                '删除后不可恢复，该 SQL 块下收藏的结果快照也会被同步删除。确定删除吗？',
                '删除确认',
                { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
            ).then(() => {
                request({
                    url: '/dbManSqlBlock/delete',
                    method: 'post',
                    data: { id: sb.id }
                }).then(res => {
                    if (res.code == 0) {
                        ElMessage({ message: '已删除', type: 'success', duration: 1500 });
                        const idx = this.rightBlocks.findIndex(x => x.id === sb.id);
                        if (idx > -1) this.rightBlocks.splice(idx, 1);
                        this.blockPage.total = Math.max(0, this.blockPage.total - 1);
                        this.$emit('total', this.blockPage.total);
                        if (this.rightBlocks.length === 0 && this.blockPage.current > 1) {
                            this.blockPage.current -= 1;
                        }
                        this.load();
                        this.$emit('changed');
                    }
                });
            }).catch(() => { });
        }
    }
};
</script>

<style>
.dbconsole-blocks-drawer .el-drawer__body {
    padding: 0;
    overflow: hidden;
}

.dbconsole-right {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 12px 16px 18px;
    background-color: #fbfcfe;
    overflow: hidden;
}

.dbconsole-right__search {
    flex: 0 0 auto;
    margin-bottom: 8px;
}

.dbconsole-right__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 52px;
}

.dbconsole-pager {
    position: absolute;
    left: 50%;
    bottom: 12px;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    padding: 2px 10px;
    border: 1px solid #dbe3ef;
    border-radius: 999px;
    background-color: rgba(255, 255, 255, 0.96);
    box-shadow: 0 8px 20px -10px rgba(28, 47, 84, 0.35);
    z-index: 2;
}

.dbconsole-title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-right__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.dbconsole-right__count {
    padding: 1px 8px;
    border: 1px solid #d6e0f0;
    border-radius: 999px;
    background-color: #fff;
    color: #2f6fed;
    font-size: 12px;
}

.dbconsole-right__switch {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
}

.dbconsole-right__switch-label {
    font-size: 12px;
    color: #8a94a6;
}

.dbconsole-right__close {
    flex: 0 0 auto;
    padding: 0 4px;
}

.dbconsole-shared {
    margin-bottom: 12px;
    border: 1px solid #cbd8e8;
    border-left: 3px solid #7d9fd0;
    border-bottom: 3px solid #7d9fd0;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 1px 3px rgba(31, 45, 61, 0.06);
    overflow: hidden;
}

.dbconsole-shared__head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
}

.dbconsole-shared__info {
    flex: 1 1 auto;
    min-width: 0;
}

.dbconsole-shared__name {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dbconsole-shared__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}

.dbconsole-shared__other {
    font-size: 12px;
    font-weight: 400;
    color: #aab4c4;
}

.dbconsole-shared__desc {
    font-size: 12px;
    color: #8a94a6;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dbconsole-shared__ops {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
}

.dbconsole-mi--share {
    color: var(--el-color-success);
}

.dbconsole-mi--delete {
    color: var(--el-color-danger);
}

.dbconsole-mi.is-disabled {
    color: var(--el-text-color-disabled);
}
</style>
